import React, { useState } from 'react';

import { observer, useSelector } from '@datagrid/state';

import { type CSTypes } from '@tf/api';
import { Box, Button, Input, Stack, TextInput, TFText, Title } from '@tf/ui';
import { fmt, T } from '@tf/utils';

import { getTenantConfigQuery } from '@/core/queries';
import { appStore } from '@/core/stores';
import { useGoogleAPI } from '@/components/internal-tools/google';
import { Log, useLogger } from '@/components/internal-tools/log';

const findOrCreateDirectory = async (name: string, parentDirId = 'root'): Promise<string> => {
	const mimeType = 'application/vnd.google-apps.folder';

	const dirsRes = await gapi.client.drive.files.list({
		q: `mimeType = '${mimeType}' and name = '${name}' and '${parentDirId}' in parents and trashed = false`,
		spaces: 'drive',
	});

	// @ts-expect-error types are wrong, response has files instead of items
	if (dirsRes.result.files.length > 0) {
		// @ts-expect-error see above
		return dirsRes.result.files[0].id;
	}

	// @ts-expect-error no types for create
	const createRes = await gapi.client.drive.files.create({ name, mimeType, parents: [parentDirId] });
	return createRes.result.id;
};

const moveToDirectory = async (fileId: string, parentDirId: string): Promise<void> => {
	const file = await gapi.client.drive.files.get({
		fileId: fileId,
		fields: 'parents',
	});

	const previousParents = file.result.parents.join(',');
	// @ts-expect-error method exists
	await gapi.client.drive.files.update({
		fileId: fileId,
		addParents: parentDirId,
		removeParents: previousParents,
		fields: 'id, parents',
	});
};

const createSpreadsheet = async (
	title: string
): Promise<{ spreadsheetId?: string; spreadsheetUrl?: string }> => {
	const createRes = await gapi.client.sheets.spreadsheets.create({
		// @ts-expect-error wrong types
		properties: { title },
	});
	return createRes.result;
};

const addSheets = async (spreadsheetId: string, sheetNames: string[]) => {
	return gapi.client.sheets.spreadsheets.batchUpdate({
		spreadsheetId: spreadsheetId,
		resource: {
			requests: sheetNames.map((sheetName) => {
				return {
					addSheet: {
						properties: { title: sheetName },
					},
				};
			}),
		},
	});
};

type Table = string[][];

const rangeFromTable = (table: Table, startRowIndex: number) => {
	if (table.length < 2) {
		throw new Error('Table should be at least two rows');
	}

	// first row usually includes type and name only, we expect the seconds row to be a header
	const headerRowLength = table[1].length;
	// letter index of column: 1 = A, 2 = B, ...
	const lastColumnIndex = String.fromCharCode(64 + headerRowLength);
	const numRows = table.length;
	const lastRowIndex = startRowIndex + numRows - 1;

	return `A${startRowIndex}:${lastColumnIndex}${lastRowIndex}`;
};

const prepareSheetValues = (sheetName: string, tables: Table[]) => {
	const data: gapi.client.sheets.ValueRange[] = [];
	let lastRow = 1;
	tables.forEach((table) => {
		data.push({
			range: `${sheetName}!${rangeFromTable(table, lastRow)}`,
			majorDimension: 'ROWS',
			values: table,
		});
		lastRow = lastRow + table.length + 1;
	});
	return data;
};

const batchUpdate = async (spreadsheetId: string, data: gapi.client.sheets.ValueRange[]) => {
	return gapi.client.sheets.spreadsheets.values.batchUpdate({
		spreadsheetId,
		resource: {
			valueInputOption: 'USER_ENTERED',
			data,
		},
	});
};

const prettifyTables = async (spreadsheetId: string, ranges: string[]) => {
	const spreadsheetRes = await gapi.client.sheets.spreadsheets.get({ spreadsheetId });

	// Convert range from A1 notation to GridRange
	const toGridRange = (range: string) => {
		const [sheetTitle, rangeInSheet] = range.split('!');
		const [start, end] = rangeInSheet.split(':');
		const sheet = spreadsheetRes.result.sheets?.find((s) => s.properties?.title === sheetTitle);
		const sheetId = sheet?.properties?.sheetId;

		return {
			sheetId,
			startColumnIndex: start.charCodeAt(0) - 65,
			startRowIndex: parseInt(start.slice(1), 10) - 1,
			endColumnIndex: end.charCodeAt(0) - 65 + 1,
			endRowIndex: parseInt(end.slice(1), 10),
		};
	};

	const columnWidthUpdates = spreadsheetRes.result.sheets!.flatMap((sheet) => {
		return [
			// First three columns (label, name, type)
			{
				updateDimensionProperties: {
					range: {
						sheetId: sheet.properties?.sheetId,
						dimension: 'COLUMNS',
						startIndex: 0,
						endIndex: 3,
					},
					properties: {
						pixelSize: 200,
					},
					fields: 'pixelSize',
				},
			},
			// Last two (tooltip, comment)
			{
				updateDimensionProperties: {
					range: {
						sheetId: sheet.properties?.sheetId,
						dimension: 'COLUMNS',
						startIndex: 5,
						endIndex: 7,
					},
					properties: {
						pixelSize: 300,
					},
					fields: 'pixelSize',
				},
			},
		];
	});

	const borderUpdates = ranges.map((range) => {
		return {
			updateBorders: {
				range: toGridRange(range),
				top: { style: 'SOLID', width: 1 },
				left: { style: 'SOLID', width: 1 },
				right: { style: 'SOLID', width: 1 },
				bottom: { style: 'SOLID', width: 1 },
				innerHorizontal: { style: 'SOLID', width: 1 },
				innerVertical: { style: 'SOLID', width: 1 },
			},
		};
	});

	const textWrapUpdates = ranges.map((range) => {
		return {
			repeatCell: {
				range: toGridRange(range),
				cell: {
					userEnteredFormat: {
						wrapStrategy: 'CLIP',
					},
				},
				fields: 'userEnteredFormat(wrapStrategy)',
			},
		};
	});

	const tableHeaderUpdates = ranges
		.map((range) => {
			// Skip the first sheet
			if (range.startsWith('Sheet1')) {
				return null;
			}

			const gridRange = toGridRange(range);

			return {
				repeatCell: {
					range: {
						...gridRange,
						endRowIndex: gridRange.startRowIndex + 2,
					},
					cell: {
						userEnteredFormat: {
							backgroundColor: {
								red: 0.74,
								green: 0.84,
								blue: 0.68,
							},
							textFormat: {
								bold: true,
							},
						},
					},
					fields: 'userEnteredFormat(backgroundColor,textFormat)',
				},
			};
		})
		.filter(T.isNotNullish);

	return gapi.client.sheets.spreadsheets.batchUpdate({
		spreadsheetId,
		resource: {
			requests: [...columnWidthUpdates, ...borderUpdates, ...textWrapUpdates, ...tableHeaderUpdates],
		},
	});
};

const enumToTable = (name: string, defs: CSTypes.GetFrontendIrResponse): Table => {
	const options = defs.options[name];
	const table = [
		['enum', name],
		['Label', 'Value'],
	];
	options.forEach((option) => {
		table.push([option.label, option.value]);
	});
	return table;
};

type StructDef = CSTypes.StructDefinition & { visual?: { preset: string } };

const structToTable = (
	name: string,
	defs: CSTypes.GetFrontendIrResponse,
	comments: Record<string, string> = {}
) => {
	const struct = defs.structs[name] as StructDef;
	const kind = struct.kind === 'default' ? 'struct' : struct.kind;
	const table = [
		[kind, name, struct.visual?.preset ?? ''],
		['Label', 'Name', 'Type', 'Multiple', 'Preset', 'Tooltip', 'Comment'],
	];

	struct.fields.forEach((field) => {
		table.push([
			field.visual.label,
			field.name,
			field.ref ?? field.type,
			// Only structs and enums can be multiple
			field.ref ? (field.isMultiple ? 'TRUE' : 'FALSE') : '',
			field.visual.preset ?? '',
			field.visual.tooltip ?? '',
			comments[field.name] ?? '',
		]);
	});

	return table;
};

type FormUsage = {
	segments: string[];
	structs: string[];
	enums: string[];
};

const groupByForms = (defs: CSTypes.GetFrontendIrResponse) => {
	const usedStructs = new Set<string>();
	const usedEnums = new Set<string>();

	const groups: Record<string, FormUsage> = {};

	const readStruct = (struct: CSTypes.StructDefinition, formUsage: FormUsage) => {
		if (usedStructs.has(struct.name)) {
			return;
		}

		usedStructs.add(struct.name);

		if (struct.kind === 'segment') {
			formUsage.segments.push(struct.name);
		} else if (struct.kind === 'default' || struct.kind === 'document') {
			formUsage.structs.push(struct.name);
		}

		struct.fields.forEach((field) => {
			if (field.kind === 'struct' && field.ref) {
				readStruct(defs.structs[field.ref], formUsage);
			}

			if (field.kind === 'enum' && field.ref && !usedEnums.has(field.ref)) {
				formUsage.enums.push(field.ref);
				usedEnums.add(field.ref);
			}
		});
	};

	defs.forms.forEach((form) => {
		groups[form.name] = { segments: [], structs: [], enums: [] };
		form.segments.forEach((segment) => {
			readStruct(defs.structs[segment.kind], groups[form.name]);
		});
	});

	return groups;
};

const scriptsToComments = (processingScripts: Record<string, string[]>) => {
	// segmentKind -> (fieldName -> comment)
	const comments: Record<string, Record<string, string>> = {};

	Object.entries(processingScripts).forEach(([segmentKind, scripts]) => {
		comments[segmentKind] = {};
		scripts.forEach((s) => {
			const rules = eval(s)({});
			rules.validation.forEach((r: { rule: string; path: string }) => {
				// Only add "required" rules for first-level fields
				if (r.rule === 'required' && !r.path.includes('.')) {
					comments[segmentKind][r.path] = 'required';
				}
			});
			rules.visual.forEach((r: { rule: string; path: string }) => {
				// Only add "hide" rules for first-level fields
				if (r.rule === 'hide' && !r.path.includes('.')) {
					if (comments[segmentKind][r.path]) {
						comments[segmentKind][r.path] += ', show if ...';
					} else {
						comments[segmentKind][r.path] = 'show if ...';
					}
				}
			});
		});
	});

	return comments;
};

export const ExportRoute: React.FC = observer(() => {
	const tenant = useSelector(() => appStore.tenant?.get());
	const defs = useSelector(() => appStore.defs.get());
	const processingScripts = useSelector(() => appStore.processingScripts.get());

	const [filename, setFilename] = useState<string>('TF-XXX');

	const { logMessages, clearLog, addLogMessage } = useLogger();

	const { getToken } = useGoogleAPI();

	const runExport = async () => {
		clearLog();

		getToken()
			.then(async () => {
				// Create spreadsheet file
				addLogMessage('Starting export...');
				const exportedDefsDirId = await findOrCreateDirectory('exported-defs');
				const tenantDefsDirId = await findOrCreateDirectory(tenant, exportedDefsDirId);
				const { spreadsheetId, spreadsheetUrl } = await createSpreadsheet(filename);
				T.assertNotNullish(spreadsheetId, 'spreadsheetId is undefined');
				T.assertNotNullish(spreadsheetUrl, 'spreadsheetUrl is undefined');
				await moveToDirectory(spreadsheetId, tenantDefsDirId);
				addLogMessage(
					<>
						New spreadsheet created:{' '}
						<a href={spreadsheetUrl} target="_blank" rel="noreferrer">
							{spreadsheetUrl}
						</a>
					</>
				);

				// Meta info
				const tenantConfig = await getTenantConfigQuery(tenant);
				await batchUpdate(
					spreadsheetId,
					prepareSheetValues('Sheet1', [
						[
							['Tenant', tenant],
							['Revision', tenantConfig.revision],
							['Default locale', tenantConfig.default_locale],
							['Export date', fmt.toDate(new Date(), { preset: 'date_struct' })],
						],
					])
				);
				addLogMessage('Added meta info');

				// Add sheets with forms
				addLogMessage('Exporting data...');
				await addSheets(
					spreadsheetId,
					defs.forms.map((f) => f.name)
				);
				// Fill all form sheets
				const formGroups = groupByForms(defs);
				const commentsFromScripts = scriptsToComments(processingScripts);
				const formSheetsUpdates = defs.forms.flatMap((form) => {
					// Form info
					const formSheetTables: Table[] = [
						[
							['form', form.name],
							['Property', 'Value'],
							['Label', form.label],
							['Order', form.order?.toString() ?? ''],
							['Segments', form.segments.map((s) => s.kind).join('\n')],
						],
					];

					formGroups[form.name].segments.forEach((segmentKind) => {
						formSheetTables.push(structToTable(segmentKind, defs, commentsFromScripts[segmentKind]));
					});
					formGroups[form.name].structs.forEach((structName) => {
						formSheetTables.push(structToTable(structName, defs));
					});
					formGroups[form.name].enums.forEach((enumName) => {
						formSheetTables.push(enumToTable(enumName, defs));
					});

					return prepareSheetValues(form.name, formSheetTables);
				});
				await batchUpdate(spreadsheetId, formSheetsUpdates);

				addLogMessage('Adjusting table styles');
				await prettifyTables(
					spreadsheetId,
					formSheetsUpdates.map((u) => u.range!)
				);

				addLogMessage('Done');
				window.open(spreadsheetUrl, '_blank');
			})
			.catch((err) => {
				addLogMessage(`ERROR: ${JSON.stringify(err)}`);
			});
	};

	return (
		<Stack gap="8px">
			<Box>
				<Title order={2}>Export to Google Sheets</Title>
				<TFText maw="60ch">
					This will ask you to authorize with your Google account and will create spreadsheet file in
					exported-defs/%tenant% directory.
				</TFText>
			</Box>

			<Box sx={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
				<Input.Wrapper label="Filename" id="filename">
					<TextInput
						id="filename"
						value={filename}
						onChange={(e) => {
							setFilename(e.target.value);
						}}
					/>
				</Input.Wrapper>
				<Button onClick={runExport}>Export</Button>
			</Box>

			<Log logMessages={logMessages} />
		</Stack>
	);
});
